import Button from "components/Button/Button";
import Card from "components/Card/Card";
import DatePicker from "components/inputs/DatePicker/DatePicker";
import TextInput from "components/inputs/TextInput/TextInput";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

type AddMedicationPreset = {
  name: string;
  dosage: string;
  frequency: string;
  date: string;
  providerName: string;
};

type AddMedicationModalProps = {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (preset: AddMedicationPreset) => Promise<void>;
};

const initialPreset: AddMedicationPreset = {
  name: "",
  dosage: "",
  frequency: "",
  date: "",
  providerName: "",
};

/*
collects structured medication fields before creating a new ai medication entry
keeps the add flow consistent with the modal-based code creation path
*/
const AddMedicationModal = ({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}: AddMedicationModalProps) => {
  const [preset, setPreset] = useState<AddMedicationPreset>(initialPreset);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPreset(initialPreset);
  }, [isOpen]);

  const isSubmitDisabled =
    isSubmitting ||
    !preset.name.trim() ||
    !preset.dosage.trim() ||
    !preset.frequency.trim();

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button
            className="allan"
            disabled={isSubmitDisabled}
            onClick={async () => {
              await onSubmit({
                name: preset.name.trim(),
                dosage: preset.dosage.trim(),
                frequency: preset.frequency.trim(),
                date: preset.date.trim(),
                providerName: preset.providerName.trim(),
              });
            }}
          >
            {isSubmitting ? "Adding..." : "Add Medication"}
          </Button>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Add Medication">
        <TextInput
          label="Medication"
          name="add-medication-name"
          placeholder="Enter medication"
          value={preset.name}
          onChange={(event) => {
            setPreset((current) => ({
              ...current,
              name: event.target.value,
            }));
          }}
        />
        <TextInput
          label="Dosage"
          name="add-medication-dosage"
          placeholder="Enter dosage"
          value={preset.dosage}
          onChange={(event) => {
            setPreset((current) => ({
              ...current,
              dosage: event.target.value,
            }));
          }}
        />
        <TextInput
          label="Frequency"
          name="add-medication-frequency"
          placeholder="Enter frequency"
          value={preset.frequency}
          onChange={(event) => {
            setPreset((current) => ({
              ...current,
              frequency: event.target.value,
            }));
          }}
        />
        <DatePicker
          label="Date"
          name="add-medication-date"
          initialDate={STRING_TO_DATE(preset.date)}
          onDateChange={(date) => {
            setPreset((current) => ({
              ...current,
              date: DATE_TO_STRING(date, "MM/dd/yyyy") ?? "",
            }));
          }}
          isClearable={false}
        />
        <TextInput
          label="Provider"
          name="add-medication-provider"
          placeholder="Enter provider"
          value={preset.providerName}
          onChange={(event) => {
            setPreset((current) => ({
              ...current,
              providerName: event.target.value,
            }));
          }}
        />
      </Card>
    </KaboodleModal>
  );
};

export default AddMedicationModal;
